import * as $ from 'jquery';

// A requestAnimationFrame wrapper/polyfill
// tslint:disable:no-unbound-method
let anim = window.requestAnimationFrame
	|| window.webkitRequestAnimationFrame
	|| (window as any).mozRequestAnimationFrame;

let stopAnim = window.cancelAnimationFrame
	|| window.webkitCancelAnimationFrame
	|| (window as any).webkitCancelRequestAnimationFrame
	|| (window as any).mozCancelAnimationFrame;
// tslint:enable:no-unbound-method

if(!anim || !stopAnim) {
	anim = function(cb) {
		return window.setTimeout(function() {
			cb(+new Date());
		}, 10);
	};
	stopAnim = window.clearTimeout;
}

export { anim, stopAnim };

interface ObjectAnimatorValue {
	[type : string] : number;
}

type AnimatorValue = number | ObjectAnimatorValue;

/**
 * The function that gets called during frames to actually do the animation
 */
interface Animator<T extends AnimatorValue> {
	(
		// A number between 0 and 1 (inclusive) of how far in time the animation has progressed.
		timeProgress : number,
		// The current animation value(s) (number or object with numbers, depending on the type of startValue).
		currentValue : T,
		// A number between 0 and 1 (inclusive) of how far in value the animation has progressed
		// (different from the first argument only if easing is non-linear).
		valueProgress : number,
	) : void;
}

/**
 * A function to calculate easing.
 * Gets passed a few additional arguments for legacy reasons.
 * Option can also be a string, in which case it is searched in $.easing.
 *
 * @param timeProgress The progress in time, between 0 and 1 (inclusive)
 *
 * @returns the progress (how much the value should change) between 0 and 1 (inclusive).
 */
export interface Easing {
	(timeProgress : number) : number;
	(timeProgress : number, currentTime : number, startValue : 0, endValue : 1, duration : number) : number;
}

/**
 * Possible options to createAnimation
 */
interface AnimationOptions<T extends AnimatorValue> {
	// How long the duration is supposed to last (in ms).
	// Default: 500
	duration : number;
	// The animator that handles setting the updated values (see Animator)
	animator : Animator<T>;
	// The easing function. Default: linear
	easing? : Easing | string;
	// A number or an object of the values to be animated with the values at the start of the animation.
	startValue? : T;
	//  A number or an object of the values to be animated with the values at the end of the animation
	endValue? : T;
	/**
	 * A function that returns the value to start the animation with.
	 * Can be passed instead of startValue to start with on-demand values.
	 * Is a convenience encapsulation of calling .start({startValue: val}) with a calculated value for val
	 */
	startValueCalculator?() : T;
	/**
	 * A function to execute when the animation has stopped.
	 *
	 * @param successful true if the animation finished successfully.
	 * When an animation has been aborted, `false` or `null` is passed
	 * (false if the abort was because the animation was restarted, null if it was stopped using handle.stop()).
	 */
	callback?(successful : boolean | null) : void;
	/**
	 * Callback that is only called when the animation has successfully completed (not having been stopped).
	 */
	finished?() : void;
}

type PassedAnimationOptions<T extends AnimatorValue> = {
	[K in keyof AnimationOptions<T>]? : AnimationOptions<T>[K]
} | undefined;

function valueIsObject(v : AnimatorValue) : v is ObjectAnimatorValue {
	return typeof v === 'object';
}

export interface AnimationHandle<T extends AnimatorValue> {
	start(startOptions? : PassedAnimationOptions<T>) : void;
	stop(fromRestart : boolean) : void;
}

/**
 * Creates an object with start/stop methods to animate stuff.
 * Uses requestAnimationFrame for performance and falls back to
 * window.setInterval (with an interval of 10 ms).
 *
 * @return a handle from which to control the animation. Contains the following methods:
 *         • start(startOptions) to start the animation with the given options.
 *         • stop() to stop the animation prematurely.
 */
export function createAnimation<T extends AnimatorValue>(
	animationOptions : PassedAnimationOptions<T>,
) : AnimationHandle<T> {
	let handle : number | null = null;
	// These are the options given to the last invocation of .start(). For use in .stop().
	let opts : AnimationOptions<T> | null = null;

	const result = {

		/**
		 * Starts the animation from startValue to endValue using the animator
		 * specified. startValue and endValue may be objects in which case all
		 * their properties will be animated.
		 */
		start(startOptions : PassedAnimationOptions<T>) {
			// Take note of the time
			let startTime : number;

			// Clear previous animations
			if(handle) {
				result.stop(true);
			}

			// Merge all options
			opts = {
				animator: Function.prototype as Animator<T>,
				duration: 500,
				...(animationOptions || {}),
				...(startOptions || {}),
			};

			// Normalize options
			if(typeof opts.easing === 'string') {
				opts.easing = ($ as any).easing[opts.easing];
			}
			if(typeof opts.easing !== 'function') {
				delete opts.easing;
			}
			const easing = opts.easing || null;
			const duration = opts.duration;
			const animator = opts.animator;
			// Handle value tweening
			let startValue : ObjectAnimatorValue = {};
			let endValue : ObjectAnimatorValue = {};
			if(opts.startValue === undefined && opts.startValueCalculator) {
				opts.startValue = opts.startValueCalculator();
			}
			if(opts.startValue === undefined) {
				throw new Error(`Animation start value ${opts.startValue} is invalid`);
			}
			if(opts.endValue === undefined) {
				throw new Error(`Animation end value ${opts.endValue} is invalid`);
			}
			const objectGiven = valueIsObject(opts.startValue);
			if(!objectGiven) {
				startValue = {_val: opts.startValue as number};
				endValue = {_val: opts.endValue as number};
			} else {
				startValue = {...(opts.startValue as ObjectAnimatorValue)};
				endValue = {...(opts.endValue as ObjectAnimatorValue)};
			}
			// Calculate work to be done
			const valueDiff : ObjectAnimatorValue = {};
			for(const i in startValue) {
				valueDiff[i] = endValue[i] - startValue[i];
			}
			handle = anim(function animate(ts) {
				startTime = startTime || ts;
				const currentTime = ts - startTime;
				const timeProgress = Math.min(1, currentTime / duration);
				let valueProgress = timeProgress;
				if(easing) {
					// Arguments beyond the first are for legacy jQuery versions
					valueProgress = easing(timeProgress, currentTime, 0, 1, duration);
				}
				let currentValue : T = ({} as any);
				for(const i in valueDiff) {
					(currentValue as ObjectAnimatorValue)[i] = startValue[i] + valueProgress * valueDiff[i];
				}
				if(!objectGiven) {
					currentValue = (currentValue as ObjectAnimatorValue)._val as T;
				}
				animator(valueProgress, currentValue, timeProgress);
				if(timeProgress < 1) {
					handle = anim(animate);
				} else {
					handle = null;
					if(opts && opts.finished) {
						opts.finished();
					}
					if(opts && opts.callback) {
						opts.callback(true);
					}
				}
			});
		},
		/**
		 * Stops a potential current animation from finishing
		 */
		stop(fromRestart : boolean) {
			if(handle) {
				stopAnim(handle);
				if(opts && opts.callback) {
					opts.callback(fromRestart ? false : null);
				}
				handle = null;
			}
		},
	};

	return result;
}




// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/util/createAnimation.ts